import React, { useState } from "react";
import { 
    Box,
    TextField,
    Button,
    Stack,
    Alert,
    Typography,
    InputAdornment,
    Checkbox,
    FormControlLabel,
} from "@mui/material";
import { useAuth } from "react-oidc-context";
import NotesIcon from "@mui/icons-material/Notes";
import EventIcon from "@mui/icons-material/Event";

export default function StatsForm() {
  const auth = useAuth();
  const token = auth.user?.access_token || auth.user?.id_token;
  const [form, setForm] = useState({
    date: "",
    course: "",
    nine_hole: false,
    score: "",
    driving_accuracy: "",
    gir: "",
    putts: "",
    lost_balls: "",
    walked: false,
    course_length: "",
    slope: "",
    rating: "",
    money: "",
    handicap: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ 
      ...form, 
      [name]: type === "checkbox" ? checked : value 
    });
    setError("");
    setMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);
    try {
      if (!token) {
        setError("You must be signed in to submit stats.");
        setLoading(false);
        return;
      }
      const res = await fetch(
        "https://elk87mfiog.execute-api.us-east-1.amazonaws.com/my-golf-stats",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify(form),
        }
      );
      const data = await res.json();
      if (res.ok) {
        setMessage("Stats submitted!");
        setForm({ course: "", score: "", driving_accuracy: "", gir: "" });
      } else {
        setError(data.message || "Failed to submit stats.");
      }
    } catch (err) {
      setError("Error: " + err.message);
    }
    setLoading(false);
  };

  return (
    <>
      {/* <img
        src="/logo192.png"
        alt="Logo192"
        width={100}
        height={100}
        style={{ display: "block", margin: "0 auto" }}
      /> */}
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        mt: 3,
        maxWidth: 400,
        mx: "auto",
        p: 3,
        borderRadius: 2,
        boxShadow: 3,
        backgroundColor: "#fff",
      }}
    >
      <Typography variant="h6" align="center" sx={{ mb: 2 }}>
        Input Stats
      </Typography>
      <Stack spacing={2}>
        {message && <Alert severity="success">{message}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          label="Date"
          name="date"
          type="date"
          value={form.date}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
          required
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EventIcon />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Course"
          name="course"
          value={form.course}
          onChange={handleChange}
          required
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.nine_hole}
              onChange={handleChange}
              name="nine_hole"
              color="primary"
            />
          }
          label="Nine Hole Round"
        />
        <TextField
          label="Score"
          name="score"
          type="number"
          value={form.score}
          onChange={handleChange}
          required
        />
        <TextField
          label="Driving Accuracy (%)"
          name="driving_accuracy"
          type="number"
          value={form.driving_accuracy}
          onChange={handleChange}
          inputProps={{ min: 0, max: 100 }}
          required
        />
        <TextField
          label="GIR (%)"
          name="gir"
          type="number"
          value={form.gir}
          onChange={handleChange}
          inputProps={{ min: 0, max: 100 }}
          required
        />
        <TextField
          label="Putts"
          name="putts"
          type="number"
          value={form.putts}
          onChange={handleChange}
          inputProps={{ min: 0, max: 100 }}
          required
        />
        <TextField
          label="Lost Balls"
          name="lost_balls"
          type="number"
          value={form.lost_balls}
          onChange={handleChange}
          inputProps={{ min: 0, max: 100 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.walked}
              onChange={handleChange}
              name="walked"
              color="primary"
            />
          }
          label="Walked"
        />
        <TextField
          label="Course Length"
          name="course_length"
          type="number"
          value={form.course_length}
          onChange={handleChange}
          inputProps={{ min: 0, max: 10000 }}
        />
        <TextField
          label="Slope"
          name="slope"
          type="number"
          value={form.slope}
          onChange={handleChange}
          inputProps={{ min: 0, max: 1000, step: "0.1" }}
        />
        <TextField
          label="Rating"
          name="rating"
          type="number"
          value={form.rating}
          onChange={handleChange}
          inputProps={{ min: 0, max: 100, step: "0.1" }}
        />
        <TextField
          label="Money Won/Lost"
          name="money"
          type="number"
          value={form.money}
          onChange={handleChange}
          inputProps={{ min: -10000, max: 10000, step: "0.01" }}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
        <TextField
          label="Handicap"
          name="handicap"
          type="number"
          value={form.handicap}
          onChange={handleChange}
          inputProps={{ min: 0, max: 100, step: "0.1" }}
        />
        <TextField
          label="Notes"
          name="notes"
          value={form.notes}
          onChange={handleChange}
          multiline
          minRows={2}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <NotesIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? "Submitting..." : "Submit Stats"}
        </Button>
      </Stack>
    </Box>
    </>
  );
}